/**
 * The dashboard header: a time-of-day greeting and the roles the visitor is
 * wearing today.
 *
 * "Welcome back, Sam" says nothing a returning user does not already know.
 * "EVENING, SAM." with "TUESDAY, 17 MARCH · PLAYER + CLUB OWNER + LEAGUE ADMIN"
 * above it tells them when they are and what the page is about to hold them
 * responsible for — one account can wear four hats, and which ones are live is
 * exactly what changes the shape of the page below.
 */

export type Persona = 'player' | 'club owner' | 'university admin' | 'league admin'

/** Ordered so the eyebrow always reads the same way for the same set of roles. */
const PERSONA_ORDER: Persona[] = ['player', 'club owner', 'university admin', 'league admin']

export interface PersonaInput {
  /** Holds at least one active roster spot. */
  onRoster: boolean
  /** Org memberships, by org type and whether the role administers it. */
  orgs: { type: string; role: string }[]
}

/** Roles that carry authority over an org, as opposed to merely belonging to it. */
const ADMIN_ROLES = new Set(['owner', 'admin', 'manager'])

export function personas(input: PersonaInput): Persona[] {
  const found = new Set<Persona>()
  if (input.onRoster) found.add('player')

  for (const org of input.orgs) {
    if (!ADMIN_ROLES.has(org.role)) continue
    if (org.type === 'league') found.add('league admin')
    else if (org.type === 'university') found.add('university admin')
    else if (org.type === 'organization') found.add('club owner')
  }

  return PERSONA_ORDER.filter((p) => found.has(p))
}

/**
 * Morning / afternoon / evening, on the visitor's own clock.
 *
 * The boundaries are the ordinary English ones rather than even thirds — nobody
 * reads 17:00 as afternoon.
 */
export function timeOfDay(now: Date): 'morning' | 'afternoon' | 'evening' {
  const hour = now.getHours()
  if (hour < 12) return 'morning'
  if (hour < 17) return 'afternoon'
  return 'evening'
}

/** "Evening, Sam." — the period and the casing are applied by the heading. */
export function greeting(name: string, now: Date): string {
  return `${capitalize(timeOfDay(now))}, ${name}.`
}

/** "Tuesday, 17 March" */
export function longDate(now: Date, locale?: string): string {
  return now.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

/** "TUESDAY, 17 MARCH · PLAYER + CLUB OWNER" — uppercasing is left to CSS. */
export function eyebrow(now: Date, roles: Persona[], locale?: string): string {
  const date = longDate(now, locale)
  return roles.length > 0 ? `${date} · ${roles.join(' + ')}` : date
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}
